/**
 * Guest → LifeScribe cross-sell funnel: touched → clicked → converted, with the
 * drop-off between each pair labelled.
 *
 * Three proportional bars rather than a chart library, for the same reason as
 * StatusFunnelCard: three values do not need a plotting engine, and this way
 * every colour is a theme palette reference.
 */

import React from 'react';
import { Box, Card, CardContent, Typography, LinearProgress, Stack } from '@mui/material';
import { num, percentage } from '../weddingFormat';
import type { ConversionFunnel } from '../weddingApi';

interface Props {
  funnel: ConversionFunnel;
  title?: string;
}

const ConversionFunnelCard: React.FC<Props> = ({ funnel, title = 'Guest conversions' }) => {
  // PREFER THE CANONICAL NAMES, FALL BACK TO THE ALIASES.
  //
  // `touched` and `clicked` are the OLD names; the server marks them "kept for
  // one release" so a stale bundle renders a number instead of `undefined`.
  // Reading them first meant this card was the reason they could never be
  // dropped — and the day they were, it would have rendered 0 → 0 → n with no
  // error anywhere. `people_touched` / `people_clicked` are the names that stay.
  const touched = num(funnel?.people_touched ?? funnel?.touched);
  const clicked = num(funnel?.people_clicked ?? funnel?.clicked);
  const converted = num(funnel?.converted);

  const steps = [
    { label: 'Saw the cross-sell', value: touched, of: touched, color: 'info' as const },
    { label: 'Clicked through', value: clicked, of: touched, color: 'warning' as const },
    { label: 'Became a member', value: converted, of: touched, color: 'success' as const },
  ];

  return (
    <Card sx={{ height: '100%', borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {touched === 0
            ? 'No guest has reached the cross-sell yet.'
            : `${percentage(converted, touched)}% of guests who saw it became members.`}
        </Typography>

        <Stack spacing={2}>
          {steps.map((step, index) => {
            const pct = percentage(step.value, step.of);
            const previous = index === 0 ? null : steps[index - 1].value;
            const dropped = previous === null ? null : previous - step.value;
            return (
              <Box key={step.label}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{step.label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {step.value}
                    {touched > 0 && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({pct}%)
                      </Typography>
                    )}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, pct)}
                  color={step.color}
                  sx={{ height: 8, borderRadius: 4 }}
                  aria-label={`${step.label}: ${step.value}`}
                />
                {dropped !== null && dropped > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    −{dropped} dropped off here
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ConversionFunnelCard;
